import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, MessageCircle, AtSign, Mail, Shield } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { timeAgo } from "@/lib/format";

const ICONS = { reply: MessageCircle, mention: AtSign, pm: Mail, report_action: Shield };

export default function NotificationsBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data.items);
      setUnread(data.unread);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [user, load]);

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setItems((it) => it.map((n) => ({ ...n, read: true })));
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-white/10 text-primary-foreground transition-colors"
          data-testid="notifications-bell"
          title="اطلاعات"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -left-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-eng font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-2 border-b border-border">
          <h3 className="urdu-naskh text-base font-semibold">اطلاعات</h3>
          {unread > 0 && (
            <Button size="sm" variant="ghost" className="text-xs urdu-naskh" onClick={markAllRead} data-testid="notif-read-all">
              سب پڑھی گئیں
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <div className="p-6 text-center urdu-naskh text-muted-foreground text-sm">— کوئی اطلاع نہیں</div>}
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            const p = n.payload || {};
            let href = "/";
            if (n.type === "reply" || n.type === "mention") href = `/t/${p.thread_id}#post-${p.post_id}`;
            else if (n.type === "pm") href = `/messages/${p.conversation_id}`;
            return (
              <Link
                key={n.id}
                to={href}
                onClick={() => setOpen(false)}
                className={`flex gap-3 px-4 py-3 border-b border-border hover:bg-muted transition-colors ${!n.read ? "bg-primary/5" : ""}`}
                data-testid={`notif-${n.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="urdu-naskh text-sm leading-snug line-clamp-2">
                    {n.type === "reply" && <><b className="font-eng text-primary">{p.by}</b> نے <b>{p.title}</b> میں جواب دیا</>}
                    {n.type === "mention" && <><b className="font-eng text-primary">{p.by}</b> نے آپ کو <b>{p.title}</b> میں ذکر کیا</>}
                    {n.type === "pm" && <><b className="font-eng text-primary">{p.by}</b> کا نجی پیغام: <span className="text-muted-foreground">{p.snippet}</span></>}
                    {n.type === "report_action" && <>آپ کی رپورٹ کا فیصلہ ہوا: <b>{p.status}</b></>}
                  </div>
                  <div className="text-[11px] font-eng text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-accent mt-2 shrink-0" />}
              </Link>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
